import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { en } from './locales/en';
import type { Dictionary } from './locales/en';
import { ru } from './locales/ru';
import { kk } from './locales/kk';
import { formatDate, formatDateLong, formatDateTime, formatNumber, formatTime } from './format';
import { INTL_LOCALE, LOCALES } from './types';
import type { Interpolations, Leaf, Locale, PluralForms, TranslationKey } from './types';

/**
 * Translation, plurals and locale-aware formatting.
 *
 * No library: the surface this product needs is keys, `{placeholder}`
 * substitution, plurals, and dates and numbers — and the keys are worth typing,
 * which is the one thing a library makes awkward.
 *
 * `Intl.PluralRules` decides which plural form applies, because Russian's four
 * forms are not something to hand-code. Dates and numbers do NOT go through
 * `Intl`: Chrome reports `kk` as supported and then formats it from the CLDR
 * root, so a Kazakh date came out as `2026 M07 24`. See format.ts.
 *
 * What is NOT here, deliberately: namespaces, lazy-loaded bundles, a translation
 * management backend. Three languages in one bundle need none of them, and each
 * would be a thing to maintain. If this ever ships twenty languages,
 * `t('a.b', vars)` is the call react-i18next makes too, so moving is mechanical.
 */

const DICTIONARIES: Record<Locale, Dictionary> = { en, ru, kk };

export type Key = TranslationKey<Dictionary>;

const STORAGE_KEY = 'locale';

/**
 * Operations here run in Russian, so an unrecognised browser language lands on
 * Russian rather than on English.
 */
const DEFAULT_LOCALE: Locale = 'ru';

const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

const readLocale = (): Locale => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Blocked storage: fall through to the browser's own preference.
  }

  // `ru-RU` and `ru` both mean Russian; only the language subtag matters here.
  const preferred = window.navigator.languages ?? [window.navigator.language];
  for (const tag of preferred) {
    const language = tag.split('-')[0]?.toLowerCase();
    if (isLocale(language)) return language;
  }

  return DEFAULT_LOCALE;
};

/** Walks a dotted path. The key is type-checked, so this cannot miss. */
const resolve = (dictionary: Dictionary, key: string): Leaf | undefined => {
  let node: unknown = dictionary;

  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }

  return typeof node === 'string' || (typeof node === 'object' && node !== null)
    ? (node as Leaf)
    : undefined;
};

const isPlural = (value: Leaf): value is PluralForms => typeof value !== 'string';

const interpolate = (template: string, values: Interpolations | undefined): string => {
  if (!values) return template;

  // A placeholder with no matching value is left as written: visible in review,
  // rather than a silent empty space in the middle of a sentence.
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  );
};

/**
 * Built once per locale and kept: constructing an `Intl` object is expensive and
 * this is asked for on every render.
 *
 * Kazakh is unaffected by the missing CLDR data that format.ts works around —
 * where a language uses one form for every count, its dictionary carries the same
 * string in `one` and `other`, so even a wrong selection reads correctly.
 */
const pluralRules = new Map<Locale, Intl.PluralRules>();

const pluralFor = (locale: Locale, count: number): Intl.LDMLPluralRule => {
  let rules = pluralRules.get(locale);

  if (!rules) {
    rules = new Intl.PluralRules(INTL_LOCALE[locale]);
    pluralRules.set(locale, rules);
  }

  return rules.select(count);
};

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /**
   * `t('flight.found', { count: 3 })`.
   *
   * `count` does double duty: it selects the plural form AND is available as a
   * `{count}` placeholder, so a caller never passes the same number twice.
   */
  t: (key: Key, values?: Interpolations & { count?: number }) => string;
  /** `24.07.2026` — the same layout in every language. */
  formatDate: (value: Date | string | number) => string;
  /** `13:10`. */
  formatTime: (value: Date | string | number, withSeconds?: boolean) => string;
  /** `24.07.2026 13:10`. */
  formatDateTime: (value: Date | string | number) => string;
  /** `24 шілде 2026`, using the active language`s own month names. */
  formatDateLong: (value: Date | string | number) => string;
  /** Grouped and pointed for the active language: `18 450` / `18,450`. */
  formatNumber: (value: number, decimals?: number) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(readLocale);

  // Screen readers pick their pronunciation from this, and the browser its
  // hyphenation. Cyrillic read aloud in English is unintelligible.
  useEffect(() => {
    document.documentElement.setAttribute('lang', locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies for this session.
    }
  }, []);

  const value = useMemo<I18nContextValue>(() => {
    const dictionary = DICTIONARIES[locale];

    // The dictionary order IS January to December; formatDateLong indexes it by
    // month number.
    const monthNames = Object.values(dictionary.months);

    return {
      locale,
      setLocale,

      t: (key, values) => {
        const entry = resolve(dictionary, key);

        if (entry === undefined) {
          // Unreachable while the locales are type-checked against `en`. Returning
          // the key beats returning an empty element: it says what is missing.
          return key;
        }

        if (isPlural(entry)) {
          const count = values?.count;

          if (count === undefined) {
            return interpolate(entry.other, values);
          }

          const form = pluralFor(locale, count);
          return interpolate(entry[form] ?? entry.other, values);
        }

        return interpolate(entry, values);
      },

      // Explicit, not Intl: see format.ts for why Kazakh forces the issue.
      formatDate,
      formatTime,
      formatDateTime,
      formatDateLong: (input) => formatDateLong(input, monthNames),
      formatNumber: (input, decimals) => formatNumber(locale, input, decimals),
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useTranslation = (): I18nContextValue => {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useTranslation must be used inside <I18nProvider>');
  }

  return context;
};
